import React from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { clearRoleSwitchContext, getRoleLandingPath, getRoleSwitchContext } from '../utils/roleSwitchFlow';
import { HostPrimaryButton, hostCardStyle } from '../components/HostSettingsCard';
import { HostOnboardingShell } from '../components/host-onboarding/HostOnboardingShell';
import { clearHostOnboardingResult, getHostOnboardingResult } from '../utils/hostOnboarding';
import { clearPendingPartnerIntent } from '../utils/partnerIntent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OUTCOME_CONTENT = {
  approved: {
    title: 'Your listing is ready',
    subtitle: 'You can now invite your guests and start earning from every stay.',
    button: 'Continue',
  },
  rejected: {
    title: 'You’re early',
    subtitle: 'Allure isn’t in your area yet. We’re expanding quickly. As soon as venues become available near your listing, we’ll notify you and activate your account.',
    button: 'Got it',
  },
  reviewing: {
    title: 'We’re reviewing your listing',
    subtitle: 'We’ll notify you once everything is ready.',
    button: 'Got it',
  },
};

export default function HostPendingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Reaching this page means the host application has been submitted —
  // wipe the fresh-signup partner intent so a later cold-launch on this
  // account uses the real default route instead of re-routing back to
  // /host-verify-listing.
  React.useEffect(() => { clearPendingPartnerIntent(); }, []);
  const roleSwitchContext = getRoleSwitchContext();
  const onboardingResult = location.state || getHostOnboardingResult() || { outcome: 'reviewing' };
  const outcome = OUTCOME_CONTENT[onboardingResult.outcome] ? onboardingResult.outcome : 'reviewing';
  const content = OUTCOME_CONTENT[outcome];

  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  })();

  const handleDone = () => {
    const roleLandingRaw = getRoleLandingPath(currentUser?.role || 'guest');
    // Mid-onboarding hosts still carry role='guest' until admin approves
    // and they switch — dropping them on /guest/home is the same class
    // of "back arrow lands on guest page" bug we just fixed on the
    // venue side. Route those users to the multi-role hub instead so
    // they can pick "Host" once approval lands.
    const safeRoleLanding = roleLandingRaw === '/guest/home' ? '/switch-role' : roleLandingRaw;
    const nextPath = roleSwitchContext?.returnPath || safeRoleLanding;
    clearRoleSwitchContext();
    clearHostOnboardingResult();
    navigate(nextPath, { replace: true });
  };

  const handlePrimaryAction = async () => {
    if (outcome === 'reviewing') {
      handleDone();
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      handleDone();
      return;
    }

    // For rejected/approved we route the user into their host listings so
    // they can manage the new (early or ready) application. Fire the
    // role switch first so the JWT is scoped to `host`; if the switch
    // fails we still fall back to a safe destination instead of leaving
    // the user on a dead-end page.
    const destination = outcome === 'rejected' ? '/host/listings' : getRoleLandingPath('host');
    try {
      const response = await axios.post(
        `${API}/auth/switch-role`,
        { target_role: 'host' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      clearRoleSwitchContext();
      clearHostOnboardingResult();
      window.location.href = destination;
    } catch (error) {
      // Switch-role can fail (e.g. already on host, race with backend
      // sync, or transient 400). Never leave the user on a dead page:
      // for the rejected outcome the listings screen is always the
      // correct destination (they need to manage the "early" listing).
      // For approved we fall back to the role home; if even that
      // doesn't apply we hand off to /switch-role rather than the
      // onboarding return path (which can loop back here).
      clearRoleSwitchContext();
      clearHostOnboardingResult();
      if (outcome === 'rejected') {
        window.location.href = '/host/listings';
      } else if (currentUser?.role === 'host' || currentUser?.host_status === 'approved') {
        window.location.href = getRoleLandingPath('host');
      } else {
        window.location.href = '/switch-role';
      }
    }
  };

  return (
    <HostOnboardingShell
      onBack={handleDone}
      pageTestId="host-pending-page"
      backTestId="host-pending-back-button"
      logoTestId="host-pending-logo"
      scrollable
      footer={<HostPrimaryButton onClick={handlePrimaryAction} dataTestId="host-pending-primary-button">{content.button}</HostPrimaryButton>}
    >
      <div style={{ ...hostCardStyle, width: '100%', padding: '24px 18px', marginTop: 12 }}>
          <p data-testid="host-pending-title" style={{ fontFamily: "'Inria Serif', serif", fontSize: 24, fontWeight: 700, color: 'var(--text)', textAlign: 'center', lineHeight: 1.12, margin: 0 }}>
            {content.title}
          </p>
          <p data-testid="host-pending-subtitle" style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, color: 'var(--text)', textAlign: 'center', lineHeight: 1.28, margin: '16px 0 0' }}>
            {content.subtitle}
          </p>
        </div>
    </HostOnboardingShell>
  );
}

